import React, { Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import Dashboard from "./components/Dashboard";
import AdminSharingReport from "./components/AdminSharingReport";
import AdminRoute from "./components/admin/AdminRoute";
// NOTE: AuthCallback, AdminFeedback, AdminScraperHealth are lazy-loaded
// below (React.lazy) — do not re-add them as static imports here. The
// Dashboard-only lazy chunks (ForeclosureMap, PortfolioView) live inside
// components/Dashboard.tsx now, not here.
import MecklenburgTaxForeclosures from "./components/MecklenburgTaxForeclosures";
import NCTaxForeclosureGuide from "./components/NCTaxForeclosureGuide";
import CountyTaxForeclosureGuide, { COUNTY_GUIDES } from "./components/CountyTaxForeclosureGuide";
import LearnHub from "./components/LearnHub";
import LearnTopicPage from "./components/LearnTopicPage";
import CountyLandingPage from "./components/CountyLandingPage";
import BiddersLandingPage from "./components/BiddersLandingPage";
import UpsetBidCalculator from "./components/UpsetBidCalculator";
import ArticlePage from "./components/ArticlePage";
import { ARTICLES } from "./content/articles";
import { COMPARISON } from "./content/comparison";
import { GlossaryHub, GlossaryTermPage } from "./components/Glossary";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import RouteAnalytics from "./components/RouteAnalytics";

// Route-level components are lazy-loaded so they land in separate webpack
// chunks users who never navigate to them don't pay for. lazyWithRetry (not
// bare React.lazy) so a stale chunk after a deploy triggers a one-time
// reload instead of crashing the route — see lib/lazyWithRetry.ts.
const PublicDashboard = lazyWithRetry(() =>
  import("./components/PublicDashboard").then((m) => ({ default: m.PublicDashboard }))
);
const PublicCountyDetail = lazyWithRetry(() =>
  import("./components/PublicDashboard").then((m) => ({ default: m.PublicCountyDetail }))
);
const AdminFeedback = lazyWithRetry(() => import("./components/AdminFeedback"));
const AdminScraperHealth = lazyWithRetry(() => import("./components/AdminScraperHealth"));
const AdminCountyConfig = lazyWithRetry(() => import("./components/AdminCountyConfig"));
const AdminDataReadiness = lazyWithRetry(() => import("./components/AdminDataReadiness"));
const AuthCallback = lazyWithRetry(() => import("./components/AuthCallback"));
const ResetPassword = lazyWithRetry(() => import("./components/ResetPassword"));
const ShareView = lazyWithRetry(() => import("./components/ShareView"));
const SharedWatchlistView = lazyWithRetry(() => import("./components/SharedWatchlistView"));
const Pricing = lazyWithRetry(() => import("./components/Pricing"));
const Redeem = lazyWithRetry(() => import("./components/Redeem"));
const TermsOfService = lazyWithRetry(() => import("./components/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("./components/PrivacyPolicy"));
const NewsletterPage = lazyWithRetry(() => import("./components/NewsletterPage"));

function App() {
  return (
    <Suspense fallback={null}>
      <RouteAnalytics />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* #2147 — dedicated password-reset landing (the redirectTo of
            resetPasswordForEmail). Separate from /auth/callback because the
            recovery signal can't be detected at the shared callback; see
            ResetPassword.tsx's header comment. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* Custom code-redemption page (#2258) — owns the redemption entry
            point so an expired/used/invalid comp code shows Parcely's own
            support-line copy instead of Stripe hosted checkout's generic
            "This promotion code is invalid" (which we cannot customize). */}
        <Route path="/redeem" element={<Redeem />} />
        <Route path="/redeem/:code" element={<Redeem />} />
        {/* Public SEO/education page — linkable in marketing posts (no auth). */}
        <Route path="/mecklenburg-tax-foreclosures" element={<MecklenburgTaxForeclosures />} />
        {/* Tier B risk-intelligence education page (#630) — explains what
            survives a tax deed, due process, the 1-year clock, title insurance. */}
        <Route path="/nc-tax-foreclosure-guide" element={<NCTaxForeclosureGuide />} />
        <Route path="/cabarrus-tax-foreclosures" element={<CountyTaxForeclosureGuide county={COUNTY_GUIDES.cabarrus} />} />
        <Route path="/gaston-tax-foreclosures" element={<CountyTaxForeclosureGuide county={COUNTY_GUIDES.gaston} />} />
        <Route path="/lincoln-tax-foreclosures" element={<CountyTaxForeclosureGuide county={COUNTY_GUIDES.lincoln} />} />
        <Route path="/upset-bid-calculator" element={<UpsetBidCalculator />} />
        {/* Direct-mail landing page for the case_bidders campaign (ADR 0033
            Month 1). noindex'd in the component itself — see BiddersLandingPage's
            own header comment for why this stays out of the sitemap. */}
        <Route path="/bidders" element={<BiddersLandingPage />} />
        {/* Monthly newsletter signup for social posts (the "monthly" list in
            digest_subscribers, migration 212). ?src= tags the campaign.
            noindex'd in the component. */}
        <Route path="/newsletter" element={<NewsletterPage />} />
        <Route path="/how-to-buy-tax-foreclosures-nc" element={<ArticlePage article={ARTICLES.howToBuy} />} />
        <Route path="/nc-foreclosure-timeline" element={<ArticlePage article={ARTICLES.timeline} />} />
        <Route path="/tax-deed-vs-tax-lien-nc" element={<ArticlePage article={ARTICLES.lienVsDeed} />} />
        <Route path="/how-to-find-pre-foreclosures-charlotte" element={<ArticlePage article={ARTICLES.preForeclosure} />} />
        <Route path="/best-nc-foreclosure-tools" element={<ArticlePage article={COMPARISON.bestTools} />} />
        <Route path="/foreclosure-com-alternative" element={<ArticlePage article={COMPARISON.foreclosureComAlt} />} />
        <Route path="/glossary" element={<GlossaryHub />} />
        <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
        <Route path="/learn" element={<LearnHub />} />
        <Route path="/learn/:propertyType" element={<LearnTopicPage />} />
        <Route path="/public" element={<PublicDashboard />} />
        <Route path="/public/:countyId" element={<PublicCountyDetail />} />
        {/* Programmatic per-county SEO landing pages (#965) — tiered by real
            lead volume, see COUNTY_LANDING_LEAD_THRESHOLD in
            lib/publicCountyStats.ts. Kept as a static import alongside the
            other lightweight content pages above, not lazy-loaded. */}
        <Route path="/foreclosures/:countyId" element={<CountyLandingPage />} />
        {/* #1541-adjacent finding, 2026-08-18: these were bare routes with no
            guard — any visitor, logged in or not, rendered the full admin
            page shell before the (correctly API-gated) data fetch failed.
            AdminRoute checks session + plan==="admin" client-side and
            redirects to "/" before children ever mount. See AdminRoute's
            own doc comment for the full incident context. */}
        <Route path="/admin/feedback" element={<AdminRoute><AdminFeedback /></AdminRoute>} />
        <Route path="/admin/scraper-health" element={<AdminRoute><AdminScraperHealth /></AdminRoute>} />
        <Route path="/admin/county-config" element={<AdminRoute><AdminCountyConfig /></AdminRoute>} />
        <Route path="/admin/sharing-report" element={<AdminRoute><AdminSharingReport /></AdminRoute>} />
        <Route path="/admin/data-readiness" element={<AdminRoute><AdminDataReadiness /></AdminRoute>} />
        {/* Public tokenized share viewer (#129). Token IS the auth — 24h
            expiry. Standalone route deliberately bypasses the Dashboard
            shell so the recipient sees a single-property focus + sign-up
            CTA, not the full app chrome. */}
        <Route path="/s/:token" element={<ShareView />} />
        {/* Public watchlist share link (#3213): revocable, trimmed view. */}
        <Route path="/w/:token" element={<SharedWatchlistView />} />
        <Route path="*" element={<Dashboard />} />
      </Routes>
    </Suspense>
  );
}

export default App;
